import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import Text from 'core-system/Text'
import PrimaryButton from 'core-system/Button/PrimaryButton'
import SecondaryButton from 'core-system/Button/SecondaryButton'
import Loading from 'core-system/Loading'
import { useDispatch, useSelector } from 'react-redux'
import { AppState } from 'redux/config/store'
import { employerActions } from 'redux/employer/employerSlice'
import { arrangeAllSegmentsForSelection } from 'features/Programs/Shared/helperFunctions'
import payCadenceService, {
  PayCadenceFormState,
  PayCadenceResponse,
} from '../payCadenceService'
import PayCadenceForm, { PayCadenceFormValues } from './PayCadenceForm'
import PayCadenceTable from './PayCadenceTable'

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`

const FormSection = styled.div`
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid ${(props) => props.theme.palette.grey.grey300};
`

const FormActions = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1.5rem;
`

const emptyFormValues = (): PayCadenceFormValues => ({
  payCadenceName: '',
  payCadenceFrequency: null,
  payCadenceDay: null,
  payCadenceDate: null,
  payCadenceDate2: null,
  selectedSegments: [],
  splitDeduction: false,
  isLastDayOfMonth: false,
})

interface PayCadenceManagementProps {
  title?: string
  description?: string
  onCadencesChange?: (cadences: PayCadenceResponse[]) => void
}

const PayCadenceManagement = ({
  title = 'Pay schedules',
  description = 'Set up pay schedules for different worksites or groups.',
  onCadencesChange,
}: PayCadenceManagementProps) => {
  const dispatch = useDispatch()
  const { worksites, allSegments } = useSelector((state: AppState) => state.employer)
  const [groupWorksites, setGroupWorksites] = useState<Record<string, string[]>>(
    {}
  )

  const [cadences, setCadences] = useState<PayCadenceResponse[]>([])
  const [isLoadingList, setIsLoadingList] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [isDeletingId, setIsDeletingId] = useState<string | null>(null)
  const [formMode, setFormMode] = useState<'hidden' | 'add' | 'edit'>('hidden')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [formValues, setFormValues] = useState<PayCadenceFormValues>(emptyFormValues)
  const [formError, setFormError] = useState<string | null>(null)
  const onCadencesChangeRef = useRef(onCadencesChange)

  useEffect(() => {
    onCadencesChangeRef.current = onCadencesChange
  }, [onCadencesChange])

  const segmentItems = useMemo(
    () => arrangeAllSegmentsForSelection(allSegments ?? []),
    [allSegments]
  )

  const availableWorksites = useMemo(
    () => (worksites ?? []).filter((worksite) => !worksite.archived),
    [worksites]
  )

  const assignedWorksiteIds = useMemo(
    () => payCadenceService.getAssignedWorksiteIds(cadences, editingId),
    [cadences, editingId]
  )

  const availableSegmentItems = useMemo(
    () =>
      payCadenceService.filterAvailableSegmentItems(
        segmentItems,
        assignedWorksiteIds,
        availableWorksites,
        allSegments ?? [],
        groupWorksites
      ),
    [
      segmentItems,
      assignedWorksiteIds,
      availableWorksites,
      allSegments,
      groupWorksites,
    ]
  )

  const loadCadences = useCallback(async () => {
    setIsLoadingList(true)
    try {
      const response = await payCadenceService.listActivePayCadences()
      const loaded = (response.data ?? []) as PayCadenceResponse[]
      setCadences(loaded)
      onCadencesChangeRef.current?.(loaded)
    } catch {
      setCadences([])
      onCadencesChangeRef.current?.([])
    } finally {
      setIsLoadingList(false)
    }
  }, [])

  useEffect(() => {
    dispatch(employerActions.getWorksites())
    dispatch(employerActions.getSegments())
    loadCadences()
  }, [dispatch, loadCadences])

  useEffect(() => {
    const customIds = (allSegments ?? [])
      .filter((segment) => segment.type === 'CUSTOM')
      .map((segment) => segment.id)
    if (customIds.length === 0) return
    payCadenceService.getGroupWorksites(customIds).then((response) => {
      setGroupWorksites(response.data?.segments || {})
    })
  }, [allSegments])

  const applyFormState = (formState: PayCadenceFormState) => {
    setFormValues({
      payCadenceName: formState.payCadenceName,
      payCadenceFrequency: formState.payCadenceFrequency,
      payCadenceDay: formState.payCadenceDay,
      payCadenceDate: formState.payCadenceDate,
      payCadenceDate2: formState.payCadenceDate2,
      isLastDayOfMonth: formState.isLastDayOfMonth,
      splitDeduction: formState.splitDeduction,
      selectedSegments: payCadenceService.worksiteIdsToSegments(
        formState.worksiteIds,
        availableWorksites,
        allSegments ?? [],
        groupWorksites,
        segmentItems
      ),
    })
  }

  const resetForm = () => {
    setFormValues(emptyFormValues())
    setFormError(null)
    setEditingId(null)
    setFormMode('hidden')
  }

  const startAdd = () => {
    setFormValues(emptyFormValues())
    setFormError(null)
    setEditingId(null)
    setFormMode('add')
  }

  const startEdit = (cadence: PayCadenceResponse) => {
    const formState = payCadenceService.formStateFromPayCadence(cadence)
    if (!formState) {
      return
    }
    setEditingId(cadence.id)
    applyFormState(formState)
    setFormError(null)
    setFormMode('edit')
  }

  const handleDelete = async (cadence: PayCadenceResponse) => {
    setIsDeletingId(cadence.id)
    try {
      await payCadenceService.deletePayCadence(cadence.id)
      if (editingId === cadence.id) {
        resetForm()
      }
      await loadCadences()
    } catch (err) {
      setFormError(payCadenceService.getPayCadenceApiErrorMessage(err))
    } finally {
      setIsDeletingId(null)
    }
  }

  const handleSave = async () => {
    const worksiteIds = payCadenceService.segmentsToWorksiteIds(
      formValues.selectedSegments,
      availableWorksites,
      allSegments ?? [],
      groupWorksites
    )

    const payload = payCadenceService.toApiPayCadencePayload({
      payCadenceName: formValues.payCadenceName,
      payCadenceFrequency: formValues.payCadenceFrequency,
      payCadenceDay: formValues.payCadenceDay,
      payCadenceDate: formValues.payCadenceDate,
      payCadenceDate2: formValues.payCadenceDate2,
      isLastDayOfMonth: formValues.isLastDayOfMonth,
      worksiteIds,
      splitDeduction: formValues.splitDeduction,
    })

    if (!payload) {
      setFormError(
        'Please select at least one group and complete all required pay schedule fields.'
      )
      return
    }

    setIsSaving(true)
    setFormError(null)
    try {
      if (formMode === 'edit' && editingId) {
        await payCadenceService.updatePayCadence(editingId, payload)
      } else {
        await payCadenceService.createPayCadence(payload)
      }
      resetForm()
      await loadCadences()
    } catch (err) {
      setFormError(payCadenceService.getPayCadenceApiErrorMessage(err))
    } finally {
      setIsSaving(false)
    }
  }

  const tableLabels = useMemo(() => {
    const groupsLabelById: Record<string, string> = {}
    const frequencyLabelById: Record<string, string> = {}
    const scheduleLabelById: Record<string, string> = {}

    cadences.forEach((cadence) => {
      groupsLabelById[cadence.id] = payCadenceService.formatGroupsLabel(
        cadence.worksites ?? [],
        availableWorksites,
        allSegments ?? [],
        groupWorksites,
        segmentItems
      )
      frequencyLabelById[cadence.id] = payCadenceService.formatPayFrequencyLabel(
        cadence.payFrequency
      )
      scheduleLabelById[cadence.id] = payCadenceService.formatPayScheduleSummary(cadence)
    })

    return { groupsLabelById, frequencyLabelById, scheduleLabelById }
  }, [cadences, availableWorksites, allSegments, groupWorksites, segmentItems])

  if (isLoadingList) {
    return <Loading />
  }

  return (
    <>
      <HeaderRow>
        <div>
          <Text variant='h4' marginBottom='0.5rem'>
            {title}
          </Text>
          <Text variant='body1'>{description}</Text>
        </div>
        {formMode === 'hidden' && (
          <PrimaryButton onClick={startAdd}>Add pay schedule</PrimaryButton>
        )}
      </HeaderRow>

      <PayCadenceTable
        cadences={cadences}
        groupsLabelById={tableLabels.groupsLabelById}
        frequencyLabelById={tableLabels.frequencyLabelById}
        scheduleLabelById={tableLabels.scheduleLabelById}
        onEdit={startEdit}
        onDelete={handleDelete}
        isDeletingId={isDeletingId}
      />

      {formMode !== 'hidden' && (
        <FormSection>
          <Text variant='h5' marginBottom='1rem'>
            {formMode === 'edit' ? 'Edit pay schedule' : 'Add pay schedule'}
          </Text>
          <PayCadenceForm
            values={formValues}
            segmentItems={availableSegmentItems}
            onChange={(partial) =>
              setFormValues((current) => ({ ...current, ...partial }))
            }
            error={formError}
          />
          <FormActions>
            <PrimaryButton onClick={handleSave} disabled={isSaving} loading={isSaving}>
              {formMode === 'edit' ? 'Save changes' : 'Add schedule'}
            </PrimaryButton>
            <SecondaryButton onClick={resetForm} disabled={isSaving}>
              Cancel
            </SecondaryButton>
          </FormActions>
        </FormSection>
      )}
    </>
  )
}

if (process.env.NODE_ENV !== 'production') {
  PayCadenceManagement.displayName = 'PayCadenceManagement'
}

export default React.memo(PayCadenceManagement)
